import { fetchAgg } from '../agg';

import { ConfluencePagesQuery, GetSpacesQuery, JiraProjectsQuery, SearchQuery } from './gql-query';
import { type KnowledgeFilterSearchResult } from './types';

type EntityType = 'space' | 'page' | 'project';

export interface KnowledgeFilterService {
	filterResults(options: {
		query: string;
		entityType: EntityType;
		confluenceSpaces?: string[];
	}): Promise<KnowledgeFilterSearchResult[]>;
	getEntitiesByAris(options: { aris: string[] }): Promise<KnowledgeFilterSearchResult[]>;
}

export class KnowledgeFilterServiceImpl implements KnowledgeFilterService {
	constructor(private siteId: string) {}

	private entityTypeToAti = (type: EntityType) => {
		switch (type) {
			case 'space':
				return 'ati:cloud:confluence:space';
			case 'page':
				return 'ati:cloud:confluence:page';
			case 'project':
				return 'ati:cloud:jira:project';
		}
	};

	filterResults = async ({
		query,
		entityType,
		confluenceSpaces = [],
	}: {
		query: string;
		entityType: EntityType;
		confluenceSpaces?: string[];
	}): Promise<KnowledgeFilterSearchResult[]> => {
		const result = await fetchAgg({
			graphQuery: SearchQuery,
			siteId: this.siteId,
			variables: { query, entityAti: this.entityTypeToAti(entityType), confluenceSpaces },
		});

		return result?.data?.search?.search?.edges
			?.map((edge: any) => edge.node)
			.map((item: any) => ({
				ari: item.ari,
				displayName: item.displayName,
				iconUrl: item.iconUrl,
				spaceKey: item.spaceKey,
				url: item.url,
			}));
	};

	getEntitiesByAris = async (options: {
		aris: string[];
	}): Promise<KnowledgeFilterSearchResult[]> => {
		let entities: Record<
			string,
			{
				responseMapper: (response: any) => KnowledgeFilterSearchResult[];
				aris: string[];
				graphQuery: string;
			}
		> = {
			space: {
				responseMapper: (response) =>
					response?.data?.confluence?.spaces?.map((space: any) => ({
						...space,
						url: space.links.base + space.links.webUi,
					})) ?? [],
				aris: [],
				graphQuery: GetSpacesQuery,
			},
			page: {
				responseMapper: (response) =>
					response?.data?.confluence?.pages?.map((page: any) => ({
						...page,
						url: page.links.base + page.links.webUi,
					})) ?? [],
				aris: [],
				graphQuery: ConfluencePagesQuery,
			},
			project: {
				responseMapper: (response) => response?.data?.jira?.jiraProjects ?? [],
				aris: [],
				graphQuery: JiraProjectsQuery,
			},
		};

		for (const ari of options.aris) {
			try {
				const resourceType = ari.split(/[:\/]/)[4];

				if (entities[resourceType]) {
					entities[resourceType].aris.push(ari);
				}
			} catch (error) {
				// Ignore invalid aris
			}
		}

		const aggPromises: Promise<KnowledgeFilterSearchResult[]>[] = [];

		for (const { aris, graphQuery, responseMapper } of Object.values(entities)) {
			if (aris.length) {
				aggPromises.push(
					fetchAgg({
						graphQuery,
						variables: { aris },
					}).then((result) => {
						return responseMapper(result).map((item) => ({
							ari: item.ari,
							displayName: item.displayName,
							iconUrl: item.iconUrl,
							spaceKey: item.spaceKey,
							url: item.url,
						}));
					}),
				);
			}
		}

		const results = await Promise.all(aggPromises);

		return results.flat();
	};
}
